// src/store/slices/genericSliceFactory.ts
import { createSlice, createAsyncThunk, type PayloadAction, type UnknownAction } from '@reduxjs/toolkit';
import { apiMethods } from '../../services/api';
import type { CrudState, ApiResponse } from '@/types';

interface GenericSliceConfig {
    name: string;
    endpoint: string;
}

/**
 * Factory that generates a full CRUD Redux slice for any resource.
 *
 * Thunks produced:
 *   fetchById         GET  /{endpoint}/:id
 *   create            POST /{endpoint}
 */
export function createGenericSlice<T extends { _id: string }>(config: GenericSliceConfig) {
    const { name, endpoint } = config;

    // ── Initial state ─────────────────────────────────────────────────────────

    const initialState: CrudState<T> = {
        items: [],
        currentItem: null,
        loading: false,
        error: null,
        total: 0,
        page: 1,
        pages: 1,
        limit: 10,
        selectedIds: [],
    };

    // ── Query helpers ─────────────────────────────────────────────────────────

    type FetchParams = {
        page?: number;
        limit?: number;
        search?: string;
        sort?: string;
        filters?: Record<string, any>;
        // Callers (e.g. FilterBar) pass extra filter keys flat — `featured`,
        // `category`, etc. — alongside the pagination params.
        [key: string]: any;
    };

    // Pagination/control keys are serialized explicitly; every other key is
    // forwarded to the backend's generic query filter. `false`/'all'/empty mean
    // "no filter" (e.g. an un-toggled Featured pill) and are omitted.
    const RESERVED_KEYS = new Set(['page', 'limit', 'search', 'sort', 'filters']);
    const skipFilterValue = (val: unknown): boolean =>
        val === undefined || val === null || val === '' || val === 'all' || val === false;

    const buildQuery = (params?: FetchParams): string => {
        const q = new URLSearchParams();
        if (params?.page) q.append('page', String(params.page));
        if (params?.limit) q.append('limit', String(params.limit));
        if (params?.search) q.append('search', params.search);
        if (params?.sort) q.append('sort', params.sort);

        // Flat filter keys passed alongside pagination (featured, category, …).
        if (params) {
            Object.entries(params).forEach(([key, val]) => {
                if (RESERVED_KEYS.has(key)) return;
                if (!skipFilterValue(val)) q.append(key, String(val));
            });
        }

        // Nested `filters` object form (kept for callers that use it).
        if (params?.filters) {
            Object.entries(params.filters).forEach(([key, val]) => {
                if (!skipFilterValue(val)) q.append(key, String(val));
            });
        }
        return q.toString();
    };

    // ── Async thunks ──────────────────────────────────────────────────────────

    const fetchActive = createAsyncThunk(
        `${name}/fetchActive`,
        async (params: FetchParams | undefined, { rejectWithValue }) => {
            try {
                const qs = buildQuery(params);
                const response = await apiMethods.get<ApiResponse<T[]>>(
                    `${endpoint}/active${qs ? `?${qs}` : ''}`
                );
                return response;
            } catch (error: any) {
                return rejectWithValue(error.message || `Failed to fetch active ${name}`);
            }
        }
    );

    const fetchById = createAsyncThunk(
        `${name}/fetchById`,
        async (id: string, { rejectWithValue }) => {
            try {
                const response = await apiMethods.get<ApiResponse<T>>(`${endpoint}/${id}`);
                return response.data;
            } catch (error: any) {
                return rejectWithValue(error.message || `Failed to fetch ${name}`);
            }
        }
    );

    const fetchBySlug = createAsyncThunk(
        `${name}/fetchBySlug`,
        async (slug: string, { rejectWithValue }) => {
            try {
                const response = await apiMethods.get<ApiResponse<T>>(`${endpoint}/slug/${slug}`);
                return response.data;
            } catch (error: any) {
                return rejectWithValue(error.message || `Failed to fetch ${name} by slug`);
            }
        }
    );

    const create = createAsyncThunk(
        `${name}/create`,
        async (data: any, { rejectWithValue }) => {
            try {
                const response = await apiMethods.post<ApiResponse<T>>(endpoint, data);
                return response.data;
            } catch (error: any) {
                return rejectWithValue(error.message || `Failed to create ${name}`);
            }
        }
    );

    // ── Slice ─────────────────────────────────────────────────────────────────

    const slice = createSlice({
        name,
        initialState,
        reducers: {
            setCurrentItem: (state, action: PayloadAction<T | null>) => {
                state.currentItem = action.payload as any;
            },
            clearError: (state) => {
                state.error = null;
            },
            setSelectedIds: (state, action: PayloadAction<string[]>) => {
                state.selectedIds = action.payload;
            },
            toggleSelectId: (state, action: PayloadAction<string>) => {
                const idx = state.selectedIds.indexOf(action.payload);
                if (idx > -1) state.selectedIds.splice(idx, 1);
                else state.selectedIds.push(action.payload);
            },
            clearSelectedIds: (state) => {
                state.selectedIds = [];
            },
        },

        extraReducers: (builder) => {

            // fetchActive ──────────────────────────────────────────────────────
            builder
                .addCase(fetchActive.pending, (state) => { state.loading = true; state.error = null; })
                .addCase(fetchActive.fulfilled, (state, { payload }) => {
                    state.loading = false;
                    const raw = payload.data;
                    state.items = (Array.isArray(raw) ? raw : raw ? [raw] : []) as any;
                    state.total = payload.pagination?.total ?? (state.items as any[]).length;
                    state.page = payload.pagination?.page ?? 1;
                    state.pages = payload.pagination?.pages ?? 1;
                    state.limit = payload.pagination?.limit ?? 10;
                })
                .addCase(fetchActive.rejected, (state, { payload }) => {
                    state.loading = false;
                    state.error = payload as string;
                });

            // fetchById ────────────────────────────────────────────────────────
            builder
                .addCase(fetchById.pending, (state) => { state.loading = true; state.error = null; })
                .addCase(fetchById.fulfilled, (state, { payload }) => {
                    state.loading = false;
                    state.currentItem = payload as any;
                })
                .addCase(fetchById.rejected, (state, { payload }) => {
                    state.loading = false;
                    state.error = payload as string;
                });

            // fetchBySlug ──────────────────────────────────────────────────────
            builder
                .addCase(fetchBySlug.pending, (state) => { state.loading = true; state.error = null; })
                .addCase(fetchBySlug.fulfilled, (state, { payload }) => {
                    state.loading = false;
                    state.currentItem = payload as any;
                })
                .addCase(fetchBySlug.rejected, (state, { payload }) => {
                    state.loading = false;
                    state.error = payload as string;
                });

            // create ───────────────────────────────────────────────────────────
            builder
                .addCase(create.pending, (state) => { state.loading = true; state.error = null; })
                .addCase(create.fulfilled, (state, { payload }) => {
                    state.loading = false;
                    state.items.unshift(payload as any);
                    state.total += 1;
                })
                .addCase(create.rejected, (state, { payload }) => {
                    state.loading = false;
                    state.error = payload as string;
                });
        },
    });

    type ActiveState = ReturnType<typeof slice.reducer> & { listRequestId?: string };

    /**
     * Filter and page changes fire a request per change, and the listing refetches
     * on every dependency. Without this a slower earlier response lands last and
     * replaces the results the visitor is actually looking at.
     */
    const reducer = (state: ActiveState | undefined, action: UnknownAction): ActiveState => {
        if ((fetchActive.fulfilled.match(action) || fetchActive.rejected.match(action)) &&
            state?.listRequestId !== action.meta.requestId) return state!;
        const next: ActiveState = {
            ...slice.reducer(state, action),
            listRequestId: state?.listRequestId,
        };
        if (fetchActive.pending.match(action)) next.listRequestId = action.meta.requestId;
        return next;
    };

    return {
        reducer,
        actions: slice.actions,
        thunks: {
            fetchActive,
            fetchById,
            fetchBySlug,
            create,
        },
    };
}